import { GAME_CONFIG, type GameConfig, type Reward } from './config';

export function xpForLevel(level: number, config: GameConfig = GAME_CONFIG): number {
  const { xpBase, xpQuadratic } = config.progression;
  const n = Math.max(1, level);
  return Math.round((xpBase * (n - 1) * n) / 2 + (xpQuadratic * (n - 2) * (n - 1) * n) / 3);
}


export function xpToNextLevel(level: number, config: GameConfig = GAME_CONFIG): number {
  return xpForLevel(level + 1, config) - xpForLevel(level, config);
}

export function levelForXp(xp: number, config: GameConfig = GAME_CONFIG): number {
  const { maxLevel } = config.progression;
  let low = 1;
  let high = maxLevel;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (xpForLevel(mid, config) <= xp) low = mid;
    else high = mid - 1;
  }
  return low;
}

export interface LevelProgress {
  level: number;
  xp: number;
  currentLevelXp: number;
  nextLevelXp: number | null;
  
  progress: number;
}

export function levelProgress(xp: number, config: GameConfig = GAME_CONFIG): LevelProgress {
  const level = levelForXp(xp, config);
  const currentLevelXp = xpForLevel(level, config);
  const nextLevelXp = level < config.progression.maxLevel ? xpForLevel(level + 1, config) : null;
  const progress =
    nextLevelXp === null ? 1 : Math.min(1, (xp - currentLevelXp) / (nextLevelXp - currentLevelXp));
  return { level, xp, currentLevelXp, nextLevelXp, progress };
}

export function levelReward(level: number, config: GameConfig = GAME_CONFIG): Reward {
  return { anima: config.progression.levelRewardAnimaPerLevel * level };
}

const COMPLETION_BOOSTERS = ['standard', 'premium', 'legendary'] as const;


export function animeCompletionReward(characterCount: number, config: GameConfig = GAME_CONFIG): Reward {
  return animeCompletionDeltaReward(0, characterCount, config);
}


export function animeCompletionDeltaReward(fromCharacters: number, toCharacters: number, config: GameConfig = GAME_CONFIG): Reward {
  const from = Math.max(0, Math.floor(fromCharacters));
  const to = Math.max(from, Math.floor(toCharacters));
  const anima = (to - from) * config.animeCompletion.animaPerCharacter;
  const boosters: NonNullable<Reward['boosters']> = {};
  let hasBoosters = false;
  for (const type of COMPLETION_BOOSTERS) {
    const perCharacters = config.animeCompletion.boosterPerCharacters[type];
    if (!perCharacters || perCharacters <= 0) continue;
    const charges = Math.floor(to / perCharacters) - Math.floor(from / perCharacters);
    if (charges <= 0) continue;
    boosters[type] = charges;
    hasBoosters = true;
  }
  return { anima, ...(hasBoosters ? { boosters } : {}) };
}
